import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from "@/components/ui";
import {
  Download,
  FileText,
  ArrowLeft,
  RefreshCw,
  Calendar,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import reportsService from "@/services/reportsService";
import { DateRangeFilter, SubsidiaryFilter } from "../filters";

/**
 * Account Ledger Report Component
 *
 * Displays detailed account ledger with transactions and running balance.
 * Navigation: Subsidiary → Chart of Accounts → Account → Ledger
 *
 * Requirements: 3.1-3.8
 */
const AccountLedger = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [filters, setFilters] = useState({
    account_id: location.state?.accountId || null,
    subsidiary_id: location.state?.subsidiaryId || "",
    date_from: "",
    date_to: "",
  });

  useEffect(() => {
    if (filters.account_id) {
      loadLedger();
    }
  }, []);

  const loadLedger = async () => {
    if (!filters.account_id) {
      return;
    }

    try {
      setLoading(true);
      const response = await reportsService.getAccountLedger(filters);

      if (response?.data?.success) {
        setLedgerData(response.data.data);
      }
    } catch (error) {
      console.error("Failed to load account ledger:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadLedger();
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const exportData = {
        reportData: ledgerData,
        reportType: "account_ledger",
        reportTitle: `Account Ledger - ${ledgerData.account.code} ${ledgerData.account.name}`,
        format,
        filters,
        auditTrail: "summary",
      };

      const response =
        format === "pdf"
          ? await reportsService.exportToPDF(exportData)
          : await reportsService.exportToExcel(exportData);

      if (response?.data?.success) {
        // Download the file
        const downloadResponse = await reportsService.downloadExport(
          response.data.data.history_id,
        );
        const url = window.URL.createObjectURL(
          new Blob([downloadResponse.data]),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", response.data.data.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-NG");
  };

  if (!filters.account_id) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">
            Please select an account to view its ledger
          </p>
          <Button onClick={() => navigate("/finance/accounts")}>
            Go to Chart of Accounts
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Account Ledger</h1>
            {ledgerData && (
              <p className="text-gray-600 mt-1">
                {ledgerData.account.code} - {ledgerData.account.name}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport("pdf")}
            disabled={exporting || !ledgerData}
          >
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport("excel")}
            disabled={exporting || !ledgerData}
          >
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SubsidiaryFilter
              value={filters.subsidiary_id}
              onChange={(value) =>
                setFilters({ ...filters, subsidiary_id: value })
              }
            />
            <DateRangeFilter
              dateFrom={filters.date_from}
              dateTo={filters.date_to}
              onDateFromChange={(value) =>
                setFilters({ ...filters, date_from: value })
              }
              onDateToChange={(value) =>
                setFilters({ ...filters, date_to: value })
              }
            />
          </div>
          <div className="mt-4">
            <Button onClick={handleApplyFilters} disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "Apply Filters"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Details */}
      {ledgerData && (
        <Card>
          <CardHeader>
            <CardTitle>Account Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Account Code</p>
                <p className="font-semibold">{ledgerData.account.code}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Account Name</p>
                <p className="font-semibold">{ledgerData.account.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Account Type</p>
                <Badge>{ledgerData.account.type}</Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600">Normal Balance</p>
                <Badge variant="secondary">
                  {ledgerData.account.normal_balance}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ledger Table */}
      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Loading ledger...</p>
          </CardContent>
        </Card>
      ) : ledgerData ? (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Transactions</CardTitle>
              <div className="text-sm text-gray-600">
                {ledgerData.lines.length} transaction(s)
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Opening Balance */}
                  <TableRow className="bg-gray-50 font-semibold">
                    <TableCell colSpan={3}>Opening Balance</TableCell>
                    <TableCell className="text-right">-</TableCell>
                    <TableCell className="text-right">-</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(ledgerData.opening_balance)}
                    </TableCell>
                  </TableRow>

                  {/* Transactions */}
                  {ledgerData.lines.map((transaction, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(transaction.date)}</TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell>
                        <span className="text-blue-600 cursor-pointer hover:underline">
                          {transaction.reference_number}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {transaction.debit_amount > 0
                          ? formatCurrency(transaction.debit_amount)
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {transaction.credit_amount > 0
                          ? formatCurrency(transaction.credit_amount)
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(transaction.running_balance)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Closing Balance */}
                  <TableRow className="bg-gray-50 font-semibold border-t-2">
                    <TableCell colSpan={3}>Closing Balance</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(ledgerData.total_debits)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(ledgerData.total_credits)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(ledgerData.closing_balance)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default AccountLedger;
